import { useEffect, useRef, useState } from "react";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { toE164 } from "@/lib/phone";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";

// Customer name (type-ahead from saved customers) + WhatsApp number; opens api.whatsapp.com/send with the receipt link prefilled.
export function SendInvoice({ saleId, defaultPhone = "", defaultName = "", defaultCustomerId = null, branchId, testid = "send-invoice" }) {
  const { session } = useAuth();
  const [name, setName] = useState(defaultName || "");
  const [to, setTo] = useState(defaultPhone || "");
  const [picked, setPicked] = useState(null);
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const skip = useRef(false);

  useEffect(() => {
    if (!defaultCustomerId) return;
    api.get(`/customers/${defaultCustomerId}`).then(({ data }) => { skip.current = true; setName(data.name); setTo(data.whatsapp || ""); setPicked({ id: data.id }); }).catch(() => {});
  }, [defaultCustomerId]);

  useEffect(() => {
    if (skip.current) { skip.current = false; return; }
    const q = name.trim();
    if (q.length < 2) { setSugs([]); return; }
    const t = setTimeout(() => api.get("/customers", { params: { q } }).then((r) => { setSugs(r.data.items); setOpen(true); }).catch(() => setSugs([])), 250);
    return () => clearTimeout(t);
  }, [name]);

  const pick = (c) => { skip.current = true; setName(c.name); setTo(c.whatsapp || ""); setPicked({ id: c.id }); setOpen(false); setSugs([]); };

  const go = async () => {
    const num = toE164(session?.business?.country, to.trim());
    if (!num) return toast.error("Enter a valid WhatsApp number with country code");
    const win = window.open("", "_blank"); // open now so popup blockers allow it
    setBusy(true);
    try {
      const { data } = await api.post(`/sales/${saleId}/share-link`, { customer_id: picked?.id || null, name: name.trim() || null, whatsapp: num },
        branchId ? { headers: { "X-Branch-Id": branchId } } : undefined);
      const url = `https://api.whatsapp.com/send?phone=${num.replace(/\D/g, "")}&text=${encodeURIComponent(data.text)}`;
      if (win) win.location.href = url; else window.location.href = url;
    } catch (e) {
      win?.close();
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="relative">
        <Input placeholder="Customer name (optional)" value={name} autoComplete="off" onBlur={() => setTimeout(() => setOpen(false), 150)} onFocus={() => sugs.length && setOpen(true)}
          onChange={(e) => { setName(e.target.value); setPicked(null); }} className="h-11" data-testid={`${testid}-name-input`} />
        {open && sugs.length > 0 && (
          <div className="absolute inset-x-0 top-full z-20 mt-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg" data-testid={`${testid}-suggestions`}>
            {sugs.map((c) => (
              <button key={c.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(c)} data-testid={`${testid}-suggestion-${c.id}`}
                className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-slate-50">
                <span className="truncate font-medium text-slate-900">{c.name}</span><span className="money shrink-0 text-xs text-slate-500">{c.masked}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Input type="tel" inputMode="tel" placeholder="Customer WhatsApp, e.g. +923001234567" value={to} onChange={(e) => setTo(e.target.value)}
          className="h-11" data-testid={`${testid}-input`} />
        <button onClick={go} disabled={!to.trim() || busy} data-testid={`${testid}-button`}
          className="flex h-11 shrink-0 items-center gap-1.5 rounded-xl bg-emerald-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-500 disabled:opacity-50">
          <MessageCircle className="h-4 w-4" />{busy ? "Opening…" : "Send invoice on WhatsApp"}
        </button>
      </div>
    </div>
  );
}
